import React, { useMemo } from 'react';
import styled from "styled-components";
import { BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { useTranslation } from "react-i18next";
import { hexToRGBA } from "@shared/utils/RandomColour";
import { useSelector } from "react-redux";
import { selectAllWhereaboutsOptions } from '@features/whereabouts/store/whereaboutsOptions.duck';
import Colours from '@shared/ui/atoms/Colours';
import { FixedWhereabouts, FixedWhereaboutsValues, FreeBusyStatus } from '@features/whereabouts/services/WhereaboutOptions';
import { iconPackPath } from "@shared/utils/WhereaboutsHelper";
import { selectIconPack } from "@shared/store/auth.duck";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';

export function SingleWhereaboutsUsage(props: Props) {
  const {label, icon, whereaboutsKey, usedDays, totalDays, subtext} = props;
  const customWhereaboutsList = useSelector(selectAllWhereaboutsOptions);
  const iconPack = useSelector(selectIconPack)
  const isCustomWhereaboutsInAccount = useMemo(() => {
    if (FixedWhereaboutsValues.includes(whereaboutsKey)) return true;
    return customWhereaboutsList.filter((cw) => cw.key === whereaboutsKey).length > 0
  }, [customWhereaboutsList, whereaboutsKey])
  const {t} = useTranslation();

  const bgColour = useMemo(() =>  hexToRGBA(props?.fillColour ?? Colours.mildGrey, 0.1), [props?.fillColour])
  const fgColour = useMemo(() =>  hexToRGBA(props?.fillColour ?? Colours.mildGrey, 1), [props?.fillColour])

  return (
    <Wrapper style={props?.style ?? {}} className={props?.className ?? ''}>
      <GraphicRow>
        {props.whereaboutsKey === FixedWhereabouts.Unknown ? <>
          <UnknownWhereaboutsIcon>
            <Icon icon={IconTypes.Time} />
          </UnknownWhereaboutsIcon>
        </> : <>
          {!props?.hideIcon && !!icon ? <WhereaboutsIcon src={iconPackPath(iconPack, icon, FreeBusyStatus.FREE)} alt={label} /> : <UnknownWhereaboutsIcon />}
        </>}
        <UsageVisualContainer>
          <BodyRegular weight={600} className={"whereaboutsUsage__title"}>{isCustomWhereaboutsInAccount ? label : `Custom whereabouts not in account (${whereaboutsKey})`}</BodyRegular>
          <LineBarBackground colour={bgColour}>
            <LineBarFill percentage={usedDays / totalDays * 100} colour={fgColour} />
          </LineBarBackground>
        </UsageVisualContainer>
      </GraphicRow>
      <UsageMetricsContainer>
        <HeadlineSmall>{usedDays} {t('days')}</HeadlineSmall>
        {subtext && <BodySmall>{subtext}</BodySmall>}
      </UsageMetricsContainer>
    </Wrapper>
  )
}

interface Props {
  totalDays: number;
  usedDays: number;
  subtext?: string;
  label: string;
  icon: string;
  whereaboutsKey: string;
  style?: any;
  hideIcon?: boolean;
  className?: string;
  fillColour?: string;
}

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  border-bottom: 1px solid ${Colours.lightGrey};
  flex-direction: column;
  margin-bottom: 10px;
  padding-bottom: 10px;
  @media (${tablet}) {
    flex-direction: row;
    margin-bottom: 14px;
    padding-bottom: 14px;
  }
`

const UsageVisualContainer = styled.div`
  flex-direction: column;
  display: flex;
  width: 80%;
  padding-right: 0;
  padding-left: 0;
  p {
    margin-bottom: 0;
  }
  @media (${tablet}) {
    padding-right: 52px;
    padding-left: 32px;
    p {
      margin-bottom: 4px;
    }
  }
`

const LineBarBackground = styled.div<any>`
  width: 100%;
  height: 8px;
  position: relative;
  background-color: ${props => props.colour};
  overflow: hidden;
  border-radius: 40px;
`

const LineBarFill = styled.div<any>`
  width: ${props => `${props.percentage}%`};
  background-color: ${props => props.colour};
  height: 100%;
  position: absolute;
`

const UsageMetricsContainer = styled.div`
  display: flex;
  margin-left: 56px;
  h3 {
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 28px;
  }
  @media (${tablet}) {
    align-self: center;
    width: 80px;
    flex-direction: column;
  }
`

const WhereaboutsIcon = styled.img`
  margin-top: 4px;
  margin-right: 16px;
  width: 40px;
  height: 40px;
  @media (${tablet}) {
    width: 10%;
    margin-right: 0;
  }
`

const UnknownWhereaboutsIcon = styled.div`
  width: 56px;
  height: 52px;
  justify-content: center;
  align-items: center;
  display: flex;
  i {
    color: ${Colours.darkGrey};
    font-size: 30px;
  }
  .icon {
    display: block;
  }
  @media (${tablet}) {
    width: 60px;
  }
`

const GraphicRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
`
